import React, { useEffect, useRef, useState } from 'react';
import { AddMenu, EventForm, AvailabilityForm } from './scheduleEditors';

// SCHEDULE — the calendar, ported whole from legacy/app.js (2026-08-23,
// phase 3 of the React port). Portals into #page-schedule. The .sg-* /
// .mon-* / .schedule-* dress is in missions.css (2026-09-21).
//
// THE CALENDAR IS EVENTS (owner call 2026-09-21). Actions, deadlines and
// habits are LAYERS you add from the foot, off by default: three daily
// habits in every column had buried the real events behind "+8 more", and a
// week of placed actions read as a wall. Ask for your work and it appears;
// the rest of the time the grid is the week's shape — off-hours, busy, focus
// blocks — and what is happening in it. The layer choice is remembered.
//
// The editors (event / availability / + Add menu) are React too —
// scheduleEditors.jsx — opened from this page's own state. The
// stale-while-revalidate cache stays the engine's
// (scheduleCacheKey/readScheduleCache/writeScheduleCache), so a React
// repaint and an engine write can never key differently.

// ONE ACCENT (2026-09-21): the eight mission hues are gone. An action is the
// app's rose wash and its mission is a word in the card.
const LAYERS_KEY = 'missions_new:calLayers';
const readLayers = () => {
  try { return { actions: false, deadlines: false, habits: false, ...JSON.parse(localStorage.getItem(LAYERS_KEY) || '{}') }; }
  catch { return { actions: false, deadlines: false, habits: false }; }
};
// a habit's occurrence — projected (virtual) or logged (a node under the habit)
const isHabitInst = (a) => !!(a.virtual || a.parent_habit_id);
const isFocusWork = (ev) => ev?.event_type === 'focus_work' || ev?.is_block === true;
const hourOf = (d) => d.getHours() + d.getMinutes() / 60;
function hmOf(d) {
  const h = d.getHours(), m = d.getMinutes();
  const ap = h >= 12 ? 'pm' : 'am';
  const h12 = ((h + 11) % 12) + 1;
  return m === 0 ? `${h12}${ap}` : `${h12}:${String(m).padStart(2, '0')}${ap}`;
}
function hourLabel(h) {
  const ap = h >= 12 && h < 24 ? 'pm' : 'am';
  return `${((h + 11) % 12) + 1} ${ap}`;
}
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const iso = localYmd;

// Greedy interval-lane assignment within a day (the legacy _schedLanes).
function assignLanes(items) {
  const sorted = [...items].sort((a, b) => (a.s - b.s) || (a.e - b.e));
  let cluster = [], clusterEnd = -Infinity;
  const flush = () => {
    const colEnds = [];
    for (const it of cluster) {
      let placed = false;
      for (let c = 0; c < colEnds.length; c++) {
        if (it.s >= colEnds[c] - 1e-9) { it.lane = c; colEnds[c] = it.e; placed = true; break; }
      }
      if (!placed) { it.lane = colEnds.length; colEnds.push(it.e); }
    }
    for (const it of cluster) it.lanes = colEnds.length;
    cluster = [];
  };
  for (const it of sorted) {
    if (cluster.length && it.s >= clusterEnd - 1e-9) { flush(); clusterEnd = -Infinity; }
    cluster.push(it); clusterEnd = Math.max(clusterEnd, it.e);
  }
  if (cluster.length) flush();
  return sorted;
}

function formatEventTime(e) {
  if (!e.start_at) return '';
  if (/T00:00:00Z$/.test(e.start_at)) return '';
  const s = new Date(e.start_at);
  if (isNaN(s)) return '';
  if (s.getHours() === 0 && s.getMinutes() === 0 && (!e.end_at || new Date(e.end_at).getTime() === s.getTime())) return '';
  const en = e.end_at ? new Date(e.end_at) : null;
  if (en && en.getTime() > s.getTime()) return `${hmOf(s)}–${hmOf(en)}`;
  return hmOf(s);
}

function weekRange(weekOffset) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dow = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() + (dow === 0 ? -6 : 1 - dow) + weekOffset * 7);
  const end = new Date(monday); end.setDate(monday.getDate() + 7);
  return { start: monday, end };
}
function monthRange(monthOffset) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const dow = first.getDay();
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() + (dow === 0 ? -6 : 1 - dow));
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const lead = Math.round((first - gridStart) / 86400000);
  const rows = Math.ceil((lead + last.getDate()) / 7);
  const gridEnd = new Date(gridStart); gridEnd.setDate(gridStart.getDate() + rows * 7);
  return { monthFirst: first, gridStart, gridEnd, rows };
}
function weekOffsetForDate(d) {
  const mondayOf = (x) => {
    const m = new Date(x); m.setHours(0, 0, 0, 0);
    const dow = m.getDay();
    m.setDate(m.getDate() + (dow === 0 ? -6 : 1 - dow));
    return m;
  };
  return Math.round((mondayOf(d) - mondayOf(new Date())) / (7 * 86400000));
}

// The month, as words on the bar; a crossing range names both.
function headingText(start, end) {
  const y1 = start.getFullYear(), y2 = end.getFullYear();
  const m1 = start.getMonth(), m2 = end.getMonth();
  const long = (d) => d.toLocaleDateString(undefined, { month: 'long' });
  const short = (d) => d.toLocaleDateString(undefined, { month: 'short' });
  if (y1 !== y2) return `${short(start)} ${y1} – ${short(end)} ${y2}`;
  if (m1 !== m2) return `${short(start)} – ${short(end)} ${y1}`;
  return `${long(start)} ${y1}`;
}

// ── The drag — legacy startScheduleDrag, same DOM mechanics ──────────────────
function startScheduleDrag(downEvent, el, data, onDone, suppressClick) {
  const id = el.dataset.id;
  if (!id || id.startsWith('virtual:')) return;
  const rect = el.getBoundingClientRect();
  const offsetX = downEvent.clientX - rect.left;
  const offsetY = downEvent.clientY - rect.top;
  let ghost = null, started = false, activeDate = null, activeTime = null;
  try { el.setPointerCapture(downEvent.pointerId); } catch {}

  const ensureStarted = () => {
    if (started) return;
    started = true;
    ghost = el.cloneNode(true);
    ghost.classList.add('ol-ghost', 'sched-ghost');
    ghost.style.width = rect.width + 'px';
    document.body.appendChild(ghost);
    el.classList.add('dragging');
    document.body.classList.add('ol-dragging');
  };
  const clearDrop = () => {
    document.querySelectorAll('.sg-col.drop-active').forEach((c) => c.classList.remove('drop-active'));
    activeDate = null; activeTime = null;
  };
  const updateDrop = (x, y) => {
    const cols = document.querySelectorAll('.sg-col[data-date]');
    let hit = null;
    for (const c of cols) {
      const r = c.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) { hit = c; break; }
    }
    clearDrop();
    if (hit) {
      hit.classList.add('drop-active');
      activeDate = hit.dataset.date;
      const cs = getComputedStyle(hit);
      const startHour = parseInt(cs.getPropertyValue('--sg-start'), 10) || 6;
      const hourPx = parseFloat(cs.getPropertyValue('--sg-hour')) || 44;
      const gridRows = parseInt(cs.getPropertyValue('--sg-rows'), 10) || (24 - startHour);
      const r = hit.getBoundingClientRect();
      const topPad = parseFloat(cs.getPropertyValue('--sg-top')) || 0;
      let mins = startHour * 60 + Math.round(((y - r.top - topPad) / hourPx) * 4) * 15;
      mins = Math.max(startHour * 60, Math.min((startHour + gridRows) * 60 - 15, mins));
      activeTime = `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
    }
  };
  const onMove = (e) => {
    if (!started) {
      const dx = e.clientX - downEvent.clientX;
      const dy = e.clientY - downEvent.clientY;
      if (dx * dx + dy * dy < 9) return;
      ensureStarted();
    }
    ghost.style.left = (e.clientX - offsetX) + 'px';
    ghost.style.top = (e.clientY - offsetY) + 'px';
    updateDrop(e.clientX, e.clientY);
  };
  const cleanup = () => {
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onUp);
    try { el.releasePointerCapture(downEvent.pointerId); } catch {}
    if (ghost) ghost.remove();
    el.classList.remove('dragging');
    document.body.classList.remove('ol-dragging');
    clearDrop();
  };
  const onUp = async () => {
    const wasDrag = started;
    const date = activeDate;
    const time = activeTime || '09:00';
    cleanup();
    if (!wasDrag) return;
    suppressClick();
    if (!date) return;
    const isEvent = el.dataset.kind === 'event';
    try {
      const startMins = parseInt(time.slice(0, 2), 10) * 60 + parseInt(time.slice(3, 5), 10);
      const mkTime = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
      if (isEvent) {
        const cur = (data.events || []).find((e) => e.id === id);
        let durMins = 60;
        if (cur && cur.start_at && cur.end_at) {
          const d = Math.round((new Date(cur.end_at) - new Date(cur.start_at)) / 60000);
          if (d > 0) durMins = d;
        }
        await window.putJson(`/api/items/${id}`, {
          value_json: {
            start_at: window.withLocalOffset(`${date}T${time}:00`),
            end_at: window.withLocalOffset(`${date}T${mkTime(startMins + durMins)}:00`),
          },
        });
      } else {
        let durMins = 45;
        const cur = (data.actions || []).find((a) => a.id === id);
        if (cur && cur.scheduled_at && cur.scheduled_end_at) {
          const d = Math.round((new Date(cur.scheduled_end_at) - new Date(cur.scheduled_at)) / 60000);
          if (d > 0) durMins = d;
        }
        await window.putJson(`/data/schedule/${id}`, {
          scheduled_at: window.withLocalOffset(`${date}T${time}:00`),
          scheduled_end_at: window.withLocalOffset(`${date}T${mkTime(startMins + durMins)}:00`),
        });
      }
      onDone();
    } catch (e) { alert(`Couldn't re-date — ${e.message}`); }
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
}

// ── The week grid — the legacy renderSchedule, computed then JSX ─────────────
function WeekGrid({ data, weekOffset, layers, refresh, suppressRef, onDayClick, onEditEvent, onEditBand }) {
  const { events, avail } = data;
  // the layers: actions and their deadlines, and projected habits, only when asked for
  const actions = (data.actions || []).filter((a) => (isHabitInst(a) ? layers.habits : layers.actions));
  const deadlines = layers.deadlines ? (data.deadlines || []) : [];
  const { start } = weekRange(weekOffset);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const now = new Date();
  const days = [];
  for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(start.getDate() + i); days.push(d); }
  const idxByKey = new Map(days.map((d, i) => [iso(d), i]));

  const bands = days.map(() => []);
  const blocks = days.map(() => []);
  const fg = days.map(() => []);
  const allday = days.map(() => []);
  const dueMarks = days.map(() => []);
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const eachDaySlice = (startD, endD, fn) => {
    let cur = new Date(startD); cur.setHours(0, 0, 0, 0);
    const last = new Date(endD); last.setHours(0, 0, 0, 0);
    while (cur <= last) {
      const idx = idxByKey.get(iso(cur));
      if (idx != null) {
        const s = sameDay(cur, startD) ? hourOf(startD) : 0;
        const e = sameDay(cur, endD) ? hourOf(endD) : 24;
        fn(idx, s, e <= s ? Math.min(24, s + 0.5) : e);
      }
      cur.setDate(cur.getDate() + 1);
    }
  };

  for (const a of (avail || [])) {
    const sd = new Date(a.start_at); if (isNaN(sd)) continue;
    const ed = a.end_at ? new Date(a.end_at) : sd;
    eachDaySlice(sd, ed, (idx, s, e) => bands[idx].push({ s, e, kind: a.kind, label: a.title || a.kind, id: a.id }));
  }
  for (const ev of (events || [])) {
    if (!ev.start_at) continue;
    const sd = new Date(ev.start_at); if (isNaN(sd)) continue;
    const ed = ev.end_at ? new Date(ev.end_at) : sd;
    const timeless = sd.getHours() === 0 && sd.getMinutes() === 0 && (!ev.end_at || new Date(ev.end_at).getTime() === sd.getTime());
    const multiDay = !sameDay(sd, ed);
    if (timeless || multiDay) {
      eachDaySlice(sd, ed, (idx) => allday[idx].push({ label: ev.title || 'event', cls: 'event', id: ev.id }));
      continue;
    }
    const idx = idxByKey.get(iso(sd));
    if (idx == null) continue;
    const s = hourOf(sd);
    const e = Math.max(s + 0.4, hourOf(ed));
    if (isFocusWork(ev)) {
      blocks[idx].push({ s, e, title: ev.title || 'focus work', id: ev.id, recurring: !!(ev.recurrence && ev.recurrence.cadence) });
    } else {
      fg[idx].push({
        s, e, id: ev.id, kind: 'event',
        cls: ev.attention === 'passive' ? 'passive' : 'event',
        title: ev.title || 'event', meta: hmOf(sd),
        recurring: !!(ev.recurrence && ev.recurrence.cadence),
      });
    }
  }
  const scheduledActionIds = new Set((actions || []).map((a) => a.id));
  for (const d of (deadlines || [])) {
    if (scheduledActionIds.has(d.id)) continue;
    if (d.due_at && d.has_due_time) {
      const dd = new Date(d.due_at); if (isNaN(dd)) continue;
      const idx = idxByKey.get(iso(dd));
      if (idx != null) dueMarks[idx].push({ h: hourOf(dd), title: d.title || 'due', hard: d.hard, id: d.id });
    } else if (d.target_date) {
      const dd = new Date(`${d.target_date}T00:00:00`); if (isNaN(dd)) continue;
      const idx = idxByKey.get(iso(dd));
      if (idx != null) allday[idx].push({ label: d.title || 'due', cls: `due${d.hard ? ' hard' : ''}`, id: d.id, node: true });
    }
  }
  for (const a of (actions || [])) {
    if (!a.scheduled_at) continue;
    const sd = new Date(a.scheduled_at); if (isNaN(sd)) continue;
    const idx = idxByKey.get(iso(sd));
    if (idx == null) continue;
    if (sd.getHours() === 0 && sd.getMinutes() === 0 && !a.scheduled_end_at) {
      allday[idx].push({
        label: a.title || 'action',
        cls: 'action' + (a.virtual ? ' is-projected' : '') + (a.status === 'completed' ? ' done' : ''),
        id: a.id, node: true,
      });
      continue;
    }
    const ed = a.scheduled_end_at ? new Date(a.scheduled_end_at) : new Date(sd.getTime() + 45 * 60000);
    const s = hourOf(sd);
    const e = Math.max(s + 0.4, sameDay(sd, ed) ? hourOf(ed) : 24);
    fg[idx].push({
      s, e, id: a.id, kind: 'action', cls: isHabitInst(a) ? 'habit' : 'action',
      title: a.title || 'action',
      meta: hmOf(sd) + (a.mission_title ? ` · ${a.mission_title}` : ''),
      done: a.status === 'completed',
      projected: !!a.virtual,
    });
  }

  // Hour window fit (deadline markers excluded, clamped in later).
  // The window is the waking day (7am–midnight) and widens only if something
  // falls outside it — a tight fit made the grid jump between weeks.
  const DEFAULT_START = 7, DEFAULT_END = 24;
  let lo = DEFAULT_START, hi = DEFAULT_END;
  const fit = (s, e) => { lo = Math.min(lo, s); hi = Math.max(hi, e == null ? s : e); };
  for (const day of fg) for (const it of day) fit(it.s, it.e);
  for (const day of blocks) for (const b of day) fit(b.s, b.e);
  const startHour = Math.max(0, Math.floor(lo));
  const endHour = Math.min(24, Math.ceil(hi));
  const rows = endHour - startHour;

  const ALLDAY_VISIBLE = 2;
  const hasAllday = allday.some((c) => c.length);
  const [alldayExpanded, setAlldayExpanded] = useState(false);

  const openEvent = (id, el) => {
    const existing = (events || []).find((x) => x.id === id);
    if (!existing) { window.openSheet?.(id); return; }
    onEditEvent(existing, el);
  };
  const openBand = (id, el) => {
    const existing = (avail || []).find((x) => x.id === id);
    if (existing) onEditBand(existing, el);
  };

  const gutter = [];
  for (let h = startHour + 1; h < endHour; h += 1) {
    gutter.push(
      <span key={h} className="sg-t" style={{ top: `calc((${h} - ${startHour})*var(--sg-hour) + var(--sg-top, 0px))` }}>
        {hourLabel(h)}
      </span>,
    );
  }
  const nowH = hourOf(now);
  const nowInWeek = days.some((d) => d.getTime() === today.getTime());
  if (nowInWeek && nowH >= startHour && nowH <= endHour) {
    gutter.push(
      <span key="now" className="sg-t sg-t-now" style={{ top: `calc((${nowH} - ${startHour})*var(--sg-hour) + var(--sg-top, 0px))` }}>
        {hmOf(now)}
      </span>,
    );
  }

  return (
    <div className="sg-stage">
      <div className="sg-cal" style={{ '--sg-start': startHour, '--sg-rows': rows }}>
        {/* the masthead — dates and the all-day strip — is STICKY: it stays
            while the hours scroll under it */}
        <div className="sg-masthead">
        <div className="sg-head">
          <div className="sg-hcell sg-corner" />
          {days.map((d) => (
            <div key={iso(d)} className={`sg-hcell${d.getTime() === today.getTime() ? ' today' : ''}`}>
              <div className="sg-dow">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
              <div className="sg-dnum">{d.getDate()}</div>
            </div>
          ))}
        </div>
        {hasAllday && (
          <div className={`sg-allday${alldayExpanded ? ' expanded' : ''}`}>
            <div className="sg-allday-lab">all-day</div>
            {allday.map((chips, di) => {
              const hiddenCount = Math.max(0, chips.length - ALLDAY_VISIBLE);
              const colToday = days[di] && days[di].getTime() === today.getTime();
              return (
                <div key={di} className={`sg-allday-col${colToday ? ' today' : ''}`}>
                  {chips.map((c, i) => (
                    <span key={`${c.id || c.label}-${i}`}
                          className={`sg-pin ${c.cls}${i >= ALLDAY_VISIBLE ? ' is-overflow' : ''}`}
                          style={c.id ? { cursor: 'pointer' } : undefined}
                          onClick={c.id ? () => (c.node ? window.openSheet?.(c.id) : openEvent(c.id)) : undefined}>
                      {c.cls.startsWith('due') ? <span className="sg-due-word">due · </span> : null}{c.label}
                    </span>
                  ))}
                  {hiddenCount ? (
                    <button type="button" className="sg-allday-more"
                            onClick={(e) => { e.stopPropagation(); setAlldayExpanded((v) => !v); }}>
                      +{hiddenCount} more
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
        </div>
        <div className="sg-grid">
          <div className="sg-gutter">{gutter}</div>
          {days.map((d, idx) => {
            const isToday = d.getTime() === today.getTime();
            const laned = assignLanes(fg[idx]);
            const nh = hourOf(now);
            return (
              <div key={iso(d)} className={`sg-col${isToday ? ' today' : ''}`} data-date={iso(d)}
                   onClick={(e) => {
                     if (e.target.closest('.sg-ev, .sg-band, .sg-duemark, .sg-now, button')) return;
                     if (suppressRef.current) { suppressRef.current = false; return; }
                     onDayClick({ x: e.clientX, y: e.clientY }, iso(d));
                   }}>
                {bands[idx].map((b) => (
                  <div key={b.id} className={`sg-band k-${b.kind}`}
                       style={{ '--s': b.s, '--e': b.e, cursor: 'pointer' }} data-id={b.id}
                       onClick={(e) => openBand(b.id, e.currentTarget)}>
                    <span className="sg-bl">{b.label}</span>
                  </div>
                ))}
                {blocks[idx].map((b) => (
                  <div key={b.id} className={`sg-ev block${b.recurring ? ' recurring' : ''}`}
                       style={{ '--s': b.s, '--e': b.e }} data-id={b.id} data-kind="event"
                       onClick={(e) => {
                         if (suppressRef.current) { suppressRef.current = false; return; }
                         openEvent(b.id, e.currentTarget);
                       }}>
                    <span className="sg-blocktitle">{b.title}</span>
                  </div>
                ))}
                {laned.map((it) => {
                  const w = 100 / (it.lanes || 1);
                  const left = (it.lane || 0) * w;
                  const draggable = it.kind === 'action' ? !it.projected : !it.recurring;
                  return (
                    <div key={`${it.id}-${it.s}`}
                         className={`sg-ev ${it.cls}${it.done ? ' done' : ''}${it.recurring ? ' recurring' : ''}`}
                         style={{
                           '--s': it.s, '--e': it.e,
                           left: `calc(${left}% + 3px)`, width: `calc(${w}% - 6px)`,
                         }}
                         data-id={it.id} data-kind={it.kind}
                         onClick={(e) => {
                           if (suppressRef.current) { suppressRef.current = false; return; }
                           if (it.kind === 'event') openEvent(it.id, e.currentTarget);
                           else if (!it.projected) window.openSheet?.(it.id);
                         }}
                         onPointerDown={(e) => {
                           if (e.button !== 0) return;
                           if (!draggable) return;
                           startScheduleDrag(e, e.currentTarget, data, refresh, () => { suppressRef.current = true; });
                         }}>
                      <span className="sg-lb">{it.title}</span>
                      <span className="sg-mt">{it.meta || ''}</span>
                    </div>
                  );
                })}
                {dueMarks[idx].map((m) => {
                  const s = Math.max(startHour + 0.2, Math.min(m.h, endHour - 0.7));
                  return (
                    <div key={m.id} className={`sg-duemark${m.hard ? ' hard' : ''}`}
                         style={{ '--s': s, ...(m.id ? { cursor: 'pointer' } : {}) }} data-id={m.id}
                         onClick={() => m.id && window.openSheet?.(m.id)}>
                      <span className="sg-duelabel"><span className="sg-due-word">due · </span>{m.title}</span>
                    </div>
                  );
                })}
                {isToday && nh >= startHour && nh <= endHour && <div className="sg-now" style={{ '--s': nh }} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── The month grid ───────────────────────────────────────────────────────────
function MonthGrid({ data, monthOffset, layers, onDrillIntoWeek }) {
  const { events } = data;
  const actions = (data.actions || []).filter((a) => !isHabitInst(a) && layers.actions);
  const deadlines = layers.deadlines ? (data.deadlines || []) : [];
  const { monthFirst, gridStart, rows } = monthRange(monthOffset);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const thisMonth = monthFirst.getMonth();

  // habits: one faint line with the count, never one line per habit — that
  // is what buried the month. Only with the habits layer on.
  const habitsByDay = new Map();
  if (layers.habits) {
    for (const a of (data.actions || [])) {
      if (!isHabitInst(a) || !a.scheduled_at) continue;
      const k = String(a.scheduled_at).slice(0, 10);
      habitsByDay.set(k, (habitsByDay.get(k) || 0) + 1);
    }
  }

  const dayItems = [];
  for (const e of (events || [])) if (!isFocusWork(e)) dayItems.push({ ...e, kind: 'event' });
  for (const a of actions) dayItems.push({
    kind: 'action', id: a.id, title: a.title, status: a.status,
    start_at: a.scheduled_at, end_at: a.scheduled_end_at || a.scheduled_at,
  });
  for (const d of deadlines) {
    const when = d.due_at || `${d.target_date}T00:00:00`;
    dayItems.push({ kind: 'due', id: d.id, title: d.title, hard: d.hard, start_at: when, end_at: when });
  }
  const byDay = new Map();
  for (const it of dayItems) {
    if (!it.start_at) continue;
    const s = new Date(it.start_at); if (isNaN(s)) continue;
    s.setHours(0, 0, 0, 0);
    const eRaw = it.end_at ? new Date(it.end_at) : new Date(it.start_at);
    const ed = new Date(eRaw); ed.setHours(0, 0, 0, 0);
    const span = Math.max(1, Math.round((ed - s) / 86400000) + 1);
    for (let i = 0; i < span; i++) {
      const d = new Date(s); d.setDate(s.getDate() + i);
      const key = iso(d);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(it);
    }
  }

  const cells = [];
  for (let i = 0; i < rows * 7; i++) {
    const d = new Date(gridStart); d.setDate(gridStart.getDate() + i);
    const items = byDay.get(iso(d)) || [];
    const sorted = [...items].sort((a, b) => (a.start_at < b.start_at ? -1 : 1));
    const MAX = 3;
    const shown = sorted.slice(0, MAX);
    const more = sorted.length - shown.length;
    const habits = habitsByDay.get(iso(d)) || 0;
    cells.push(
      <div key={i}
           className={`mon-cell${d.getMonth() === thisMonth ? '' : ' dim'}${d.getTime() === today.getTime() ? ' today' : ''}`}
           onClick={() => onDrillIntoWeek(new Date(d))}>
        <div className="mon-day-num">{d.getDate()}</div>
        {shown.map((it, j) => {
          const cls = it.kind === 'action' ? ` action${it.status === 'completed' ? ' done' : ''}` : it.kind === 'due' ? ` due${it.hard ? ' hard' : ''}` : it.attention === 'passive' ? ' event passive' : ' event';
          return (
            <div key={`${it.id}-${j}`} className={`mon-chip${cls}`}>
              <i className="mon-dot" />
              <span className="mon-chip-title">{it.kind === 'due' ? <span className="sg-due-word">due · </span> : null}{it.title || 'untitled'}</span>
            </div>
          );
        })}
        {more > 0 && <div className="mon-more">+{more} more</div>}
        {habits > 0 && <div className="mon-habits">{habits} habit{habits === 1 ? '' : 's'}</div>}
      </div>,
    );
  }
  return (
    <div className="month-grid">
      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((w) => <div key={w} className="month-weekday">{w}</div>)}
      {cells}
    </div>
  );
}

export default function Schedule({ active }) {
  const [view, setView] = useState('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [data, setData] = useState(null); // { events, actions, avail, deadlines }
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0);
  const [editor, setEditor] = useState(null); // {kind:'menu'|'event'|'avail', at?, …}
  const [layers, setLayersState] = useState(readLayers);
  const setLayer = (k) => setLayersState((cur) => {
    const next = { ...cur, [k]: !cur[k] };
    try { localStorage.setItem(LAYERS_KEY, JSON.stringify(next)); } catch {}
    return next;
  });
  const suppressRef = useRef(false);
  // Esc closes whichever editor is open — the form used to sit there until "cancel"
  useEffect(() => {
    if (!editor) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setEditor(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editor]);

  useEffect(() => {
    const again = () => setEpoch((n) => n + 1);
    document.addEventListener('missions:refresh-schedule', again);
    return () => document.removeEventListener('missions:refresh-schedule', again);
  }, []);
  useEffect(() => { if (active) setEpoch((n) => n + 1); }, [active]);

  const isMonth = view === 'month';
  const wr = weekRange(weekOffset);
  const mr = monthRange(monthOffset);

  useEffect(() => {
    if (!active || !epoch) return undefined;
    let live = true;
    // stale-while-revalidate through the ENGINE's scoped cache
    const startIso = isMonth ? iso(mr.gridStart) : iso(wr.start);
    const cacheKey = window.scheduleCacheKey?.(view, startIso);
    const cached = cacheKey ? window.readScheduleCache?.(cacheKey) : null;
    if (cached) {
      setData({ events: cached.events || [], actions: cached.actions || [], avail: cached.avail || [], deadlines: cached.deadlines || [] });
    }
    // pad a day either side — the server reads the range at ITS OWN midnight
    const padStart = isMonth
      ? iso(new Date(mr.gridStart.getTime() - 86400000))
      : iso(new Date(wr.start.getTime() - 86400000));
    const padEnd = isMonth
      ? iso(new Date(mr.gridEnd.getTime() + 86400000))
      : iso(new Date(wr.end.getTime() + 86400000));
    window.fetchJson(`/api/events?range_start=${padStart}&range_end=${padEnd}`)
      .then((res) => {
        if (!live) return;
        // The agent's context reads this week's events. chatContext() is
        // synchronous, so the surface publishes what it has rather than the
        // context fetching — and until this call existed the global it reads was
        // never even declared, which threw and killed every agent message here.
        window.setScheduleEvents?.(res.events || []);
        const next = {
          events: res.events || [],
          actions: res.scheduled_actions || [],
          avail: res.availability || [],
          deadlines: res.deadlines || [],
        };
        setData(next);
        setError(null);
        if (cacheKey) window.writeScheduleCache?.(cacheKey, { events: next.events, actions: next.actions, avail: next.avail, deadlines: next.deadlines });
      })
      .catch((e) => { if (live && !cached) setError(e.message); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, epoch, view, weekOffset, monthOffset]);

  const refresh = () => setEpoch((n) => n + 1);
  const initialDate = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (isMonth) return (today >= mr.monthFirst && today < mr.gridEnd) ? iso(today) : iso(mr.monthFirst);
    return (today >= wr.start && today < wr.end) ? iso(today) : iso(wr.start);
  };

  const step = (n) => (isMonth ? setMonthOffset((o) => (n === 0 ? 0 : o + n)) : setWeekOffset((o) => (n === 0 ? 0 : o + n)));
  const heading = isMonth ? headingText(mr.monthFirst, mr.monthFirst) : headingText(wr.start, new Date(wr.end.getTime() - 86400000));
  // where the editor opens: beside the thing you clicked, or where you clicked
  const anchorFrom = (el, pt) => {
    if (el?.getBoundingClientRect) { const r = el.getBoundingClientRect(); return { x: r.right + 8, y: r.top, alt: r.left - 8 }; }
    if (pt) return { x: pt.x + 8, y: pt.y, alt: pt.x - 8 };
    return null;
  };

  return (
    <>
      {/* ONE LINE OF CHROME (2026-09-21). The toolbar tray, the 40px month
          inscription and the day headers were ~290px before 8 AM. */}
      <div className="schedule-bar">
        <h1 className="schedule-month text-title">{heading}</h1>
        <span className="schedule-nav">
          <button type="button" aria-label="Previous" onClick={() => step(-1)}>‹</button>
          <button type="button" aria-label="Next" onClick={() => step(1)}>›</button>
        </span>
        <button type="button" className="schedule-today" onClick={() => step(0)}>today</button>
        <span className="schedule-spacer" />
        <span className="schedule-view-toggle">
          <button type="button" className={view === 'week' ? 'on' : ''} onClick={() => setView('week')}>Week</button>
          <button type="button" className={view === 'month' ? 'on' : ''} onClick={() => setView('month')}>Month</button>
        </span>
        <button className="add-event-btn" type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const r = e.currentTarget.getBoundingClientRect();
                  setEditor({ kind: 'menu', at: { rect: { bottom: r.bottom, right: r.right } }, dateIso: initialDate() });
                }}>
          + add
        </button>
      </div>

      {/* THE EDITOR IS A POPOVER by the thing you clicked, never a band over
          the toolbar. Esc closes it (above). */}
      {(editor?.kind === 'event' || editor?.kind === 'avail') && (
        <SchedPopover anchor={editor.anchor} onClose={() => setEditor(null)}>
          {editor.kind === 'event'
            ? <EventForm dateIso={editor.dateIso} existing={editor.existing}
                         onDone={() => { setEditor(null); refresh(); }}
                         onClose={() => setEditor(null)} />
            : <AvailabilityForm dateIso={editor.dateIso} availKind={editor.availKind} existing={editor.existing}
                                onDone={() => { setEditor(null); refresh(); }}
                                onClose={() => setEditor(null)} />}
        </SchedPopover>
      )}
      {editor?.kind === 'menu' && (
        <AddMenu at={editor.at} dateIso={editor.dateIso}
                 onPick={(pick) => setEditor({ ...pick, anchor: anchorFrom(null, editor.at.rect ? { x: editor.at.rect.right - 320, y: editor.at.rect.bottom + 8 } : editor.at) })}
                 onClose={() => setEditor(null)} />
      )}

      <div id="schedule-body-react">
        {error && <div className="missions-empty">Couldn't load — {error}</div>}
        {!error && !data && <div className="loading">loading…</div>}
        {!error && data && (isMonth
          ? <MonthGrid data={data} monthOffset={monthOffset} layers={layers}
                       onDrillIntoWeek={(d) => { setWeekOffset(weekOffsetForDate(d)); setView('week'); }} />
          : <WeekGrid data={data} weekOffset={weekOffset} layers={layers} refresh={refresh} suppressRef={suppressRef}
                      onDayClick={(pt, dateIso) => setEditor({ kind: 'menu', at: pt, dateIso })}
                      onEditEvent={(existing, el) => setEditor({ kind: 'event', dateIso: existing.start_at ? existing.start_at.slice(0, 10) : initialDate(), existing, anchor: anchorFrom(el) })}
                      onEditBand={(existing, el) => setEditor({ kind: 'avail', availKind: existing.kind, dateIso: existing.start_at ? existing.start_at.slice(0, 10) : initialDate(), existing, anchor: anchorFrom(el) })} />)}
      </div>

      {/* THE LAYERS. The calendar is events; your work is there when you ask. */}
      {data && (
        <div className="schedule-foot">
          <span className="schedule-foot-lab">show</span>
          {[['actions', 'actions'], ['deadlines', 'deadlines'], ['habits', 'habits']].map(([k, label]) => (
            <button key={k} type="button" aria-pressed={layers[k]} onClick={() => setLayer(k)}>
              {layers[k] ? '−' : '+'} {label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// The editor's popover: fixed in the stage (inside the shell, fixed means
// stage-fixed — CLAUDE-FOR-DESIGN §4), placed beside its anchor and kept
// inside the window; click-away closes it.
function SchedPopover({ anchor, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const W = el.offsetWidth, H = el.offsetHeight;
    let x = anchor ? anchor.x : (window.innerWidth - W) / 2;
    let y = anchor ? anchor.y : 120;
    if (x + W > window.innerWidth - 12) x = anchor ? anchor.alt - W : window.innerWidth - W - 12;
    if (x < 12) x = 12;
    if (y + H > window.innerHeight - 12) y = Math.max(12, window.innerHeight - H - 12);
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }, [anchor]);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const t = setTimeout(() => document.addEventListener('pointerdown', away), 0);
    return () => { clearTimeout(t); document.removeEventListener('pointerdown', away); };
  }, [onClose]);
  return <div ref={ref} className="sched-pop">{children}</div>;
}
